// /src/components/KoFiModal.jsx

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../authContext';
import { useSecurity } from '../SecurityContext';
import DonatePanelMemberView from './donate/DonatePanelMemberView';
import KofiDonationLink from './donate/KofiDonationLink';
import KofiEmbedPanel from './donate/KofiEmbedPanel';
import KofiSupportOverview from './donate/KofiSupportOverview';
import RosterProfileThemeCustomizeModal from '../pages/private/members/rosterTools/components/rosterFileTools/RosterProfileThemeCustomizeModal';
import {
  fetchDonatePanelPublic,
  fetchKofiMySupportStatus,
} from '../pages/private/admin/PrivateDocuments/donatePanel/donatePanelApi';
import { SUPPORT_CHOICE_STATUS_CHANGED } from './donate/supportChoice/supportChoiceEvents';

function KoFiModal({ isOpen, onClose }) {
  const { isLoggedIn } = useAuth();
  const { isViewAsActive, viewAsUserId, viewAsLoading } = useSecurity();
  const modalRef = useRef(null);
  const [panel, setPanel] = useState(null);
  const [support, setSupport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [themeModalOpen, setThemeModalOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setThemeModalOpen(false);
      return undefined;
    }
    if (isViewAsActive && viewAsLoading) return undefined;

    let cancelled = false;
    const loadPanel = async () => {
      setLoading(true);
      setError('');
      try {
        const [panelData, supportData] = await Promise.all([
          fetchDonatePanelPublic(),
          isLoggedIn ? fetchKofiMySupportStatus() : Promise.resolve(null),
        ]);
        if (!cancelled) {
          setPanel(panelData?.ok ? (panelData.data || null) : null);
          setSupport(supportData?.ok ? (supportData.data || null) : null);
        }
      } catch (loadError) {
        console.error('Error fetching donate panel:', loadError);
        if (!cancelled) {
          setPanel(null);
          setSupport(null);
          setError('Could not load your Ko-fi support details.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPanel();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isLoggedIn, isViewAsActive, viewAsUserId, viewAsLoading]);

  useEffect(() => {
    if (!isOpen || !isLoggedIn) return undefined;

    const onStatusChanged = async (event) => {
      if (event?.detail) {
        setSupport((prev) => ({ ...(prev || {}), ...event.detail }));
        return;
      }
      try {
        const supportData = await fetchKofiMySupportStatus();
        if (supportData?.ok) setSupport(supportData.data || null);
      } catch (_) {
        /* keep previous support snapshot */
      }
    };

    window.addEventListener(SUPPORT_CHOICE_STATUS_CHANGED, onStatusChanged);
    return () => window.removeEventListener(SUPPORT_CHOICE_STATUS_CHANGED, onStatusChanged);
  }, [isOpen, isLoggedIn]);

  const handleOverlayClick = (e) => {
    if (themeModalOpen) return;
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {createPortal(
        <div className="kofi-modal-overlay" onClick={handleOverlayClick}>
          <div className="kofi-modal-content" ref={modalRef}>
            <div className="kofi-modal-header">
              <h3 className="kofi-modal-h3">
                {isViewAsActive ? 'Donation panel (view as)' : 'Donation panel'}
              </h3>
              <button type="button" className="kofi-close-button" onClick={onClose}>x</button>
            </div>

            <div className="kofi-modal-body">
              <div
                className="donate-panel-modal-content"
                data-resource="public.donation-panel"
                data-action="read"
                data-route="/api/public/donate-panel"
                data-description="Donation Panel"
              >
                {loading || (isViewAsActive && viewAsLoading) ? (
                  <p className="donate-panel-modal-content__loading">Loading…</p>
                ) : (
                  <DonatePanelMemberView panel={panel} />
                )}
              </div>

              <div className="kofi-widget-column">
                {error ? <p className="kofi-donation-link__msg kofi-donation-link__msg--error">{error}</p> : null}
                {isLoggedIn && support ? (
                  <KofiSupportOverview
                    support={support}
                    rewards={Array.isArray(support.nextRewards) ? support.nextRewards : []}
                  />
                ) : null}
                <KofiDonationLink />
                {isLoggedIn ? (
                  <button
                    type="button"
                    className="kofi-modal__themes-btn"
                    onClick={() => setThemeModalOpen(true)}
                    data-resource="private.settings.account"
                    data-action="read"
                    data-route="/api/user/settings/profile-theme"
                    data-description="Browse profile themes from donation panel"
                  >
                    Browse profile themes
                  </button>
                ) : null}
                <KofiEmbedPanel />
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
      <RosterProfileThemeCustomizeModal
        isOpen={themeModalOpen}
        onClose={() => setThemeModalOpen(false)}
      />
    </>
  );
}

export default KoFiModal;
